import React, { useState, useEffect } from 'react';
import { connect, useSelector } from 'react-redux';
import {
  IAadharVerificationScreen,
  IAadharVerificationPayload,
  IStoreTransactionDetails
} from '../../utils/interface';
import { CommonButton, ModalFactory, StepHeader } from '@Components';
import {
  AADHAR_VERIFICATION,
  AADHAR_RETRY_COUNT,
  NIUM_FOREX_CURRENT_SCREEN,
  screenSettings,
  totalSteps
} from '../../utils/constants';
import { activateLoader, disableLoader } from '@Redux/actions/ui-trigger';
import { ButtonContainer } from './style';
import { FormContainer, ParentContainer } from '../../styles';
import { CLOSE_POPUP_ICON, DIGI_LOCKER_ADHAAR_ICON, ID_CARD_FILL_ICON } from '@Images';
import AadharSuccess from './aadhar-verified';
import RetryAadharModal from './retry-aadhar-verification-modal';
import AadharNotLinkedModal from './aadhar-not-linked-modal';
import {
  getCaptureLink,
  changeNiumForexScreen,
  getUserAadharStatus,
  storeTransactionDetails,
  getNiumForexUserDetails,
  storeCafDocumentChanged,
  storeReverifyAadharFlag
} from '@Redux/actions/nium-forex';
import { NF_CALLBACK_URLS, NF_SCREENS } from '../../root/screenConstants';
import { IStore } from '../../../../store.type';
import CancelPaymentModal from '../../utils/cancel-payment-modal';
import {
  APPROVED,
  FAILED,
  VERIFIED,
  IN_PROGRESS,
  FLOW_TYPE,
  MISMATCH
} from '../../../../utils/constants';
import { NF_AADHAR_EVENT } from '../../../../utils/constants/eventsConstants';
import { getUserProfileData } from '../../../../redux/actions/user-state/user-state-action';
import { selectNiumForexReducer } from '../../../../redux/selectors';
import { logEvent } from '../../../../utils/events/logUserEvents';
import { LOG_TYPE } from '../../../../utils/events/eventsConstants';

const AadharVerificationScreen = (props: IAadharVerificationScreen) => {
  const {
    disableLoader,
    getCaptureLink,
    getUserAadharStatus,
    dispatch,
    tempTransactionId,
    storeTransactionDetails,
    reVerifyAadhar,
    cafDocumentFlag,
    status,
    getUserProfileData,
    storeCafDocumentChanged,
    storeReverifyAadharFlag
  } = props;

  const [showMainScreen, setShowMainScreen] = useState<boolean>(true);
  const [tempTxnId, setTempTxnId] = useState<number>(tempTransactionId);
  const [loading, setLoading] = useState(false);
  const [userAadharStatusApproved, setUserAadharStatusApproved] = useState(false);

  const currentURL = window.location.href;
  const stepperData = screenSettings[NF_SCREENS.aadhar_verification as keyof typeof screenSettings];
  const reKycRequired = useSelector(selectNiumForexReducer)?.userDetails?.rekyc_required;

  const handleLogEvents = () => {
    logEvent(
      NF_AADHAR_EVENT,
      FLOW_TYPE.TRANSACTION,
      { pageName: NF_SCREENS.aadhar_verification },
      LOG_TYPE.CLEVER_TAP
    );
  };

  const handleIsAadharToBeReInitiate = () => {
    if (tempTransactionId && userAadharStatusApproved && !cafDocumentFlag) {
      return true;
    }
    return false;
  };

  const handleSubmit = async () => {
    handleLogEvents();
    localStorage.removeItem(NIUM_FOREX_CURRENT_SCREEN);
    sessionStorage.removeItem(AADHAR_RETRY_COUNT);
    const payload: IAadharVerificationPayload = {
      //In case of Aadhar failure, tempTransactionId will be undefined thats why need to capture from url.
      tempTransactionId: tempTransactionId || tempTxnId,
      isDocumentChange: cafDocumentFlag,
      isAadharVerificationReinitiated:
        reVerifyAadhar || handleIsAadharToBeReInitiate() ? true : false
    };

    dispatch(activateLoader());
    const response: any = await dispatch(getCaptureLink(payload));
    if (response?.success && response?.captureLink) {
      dispatch(getNiumForexUserDetails());
      localStorage.setItem(NIUM_FOREX_CURRENT_SCREEN, NF_SCREENS.aadhar_verification);
      //Redirect Link Encoded by BE.
      window.open(decodeURIComponent(response?.captureLink || ''), '_self');
    }
    dispatch(disableLoader());
  };

  const handleAadharMobileNotLinked = async () => {
    //@ts-ignore
    return await ModalFactory.builder?.(
      (onClose: any) => <AadharNotLinkedModal handleGotIt={() => onClose(true)} />,
      'nium-forex aadhar-not-linked insta-modal center-modal',
      true
    );
  };

  const handleCloseAadharVerification = async () => {
    // @ts-ignore
    const closeAadharVerificationFlag: boolean = await ModalFactory.builder?.(
      (onClose: any) => (
        <CancelPaymentModal onContinue={() => onClose(false)} onConfirm={() => onClose(true)} />
      ),
      'center-modal insta-modal',
      true
    );
    if (closeAadharVerificationFlag) {
      sessionStorage.removeItem(AADHAR_RETRY_COUNT);
      localStorage.removeItem(NIUM_FOREX_CURRENT_SCREEN);
      dispatch(storeCafDocumentChanged(false));
      dispatch(changeNiumForexScreen(NF_SCREENS.send_money));
    }
  };

  const handleGetUserAadharStatus = async () => {
    const aadharRetryCount: number = Number(sessionStorage.getItem(AADHAR_RETRY_COUNT) || 0) || 0;
    if (aadharRetryCount < 3) {
      if (tempTxnId) {
        const params: IAadharVerificationPayload = {
          tempTransactionId: tempTxnId,
          isDocumentChange: cafDocumentFlag
        };
        dispatch(activateLoader());
        const response: any = await dispatch(getUserAadharStatus(params));
        if (response?.aadharStatus === VERIFIED) {
          dispatch(disableLoader());
          /*once aadhar verified, remove current screen from ls, so when user goes to pan and select bank,
          callback url will still be there and if user tries to refresh, in root component aadhar callback url
          will match but currentscreen will not be there.
          User need to click pending transaction to continue.
          */
          localStorage.removeItem(NIUM_FOREX_CURRENT_SCREEN);
          setLoading(false);
          setTimeout(() => {
            const transactionDetails: IStoreTransactionDetails = {
              tempTransactionId: tempTxnId
            };
            dispatch(storeTransactionDetails(transactionDetails));
            dispatch(changeNiumForexScreen(NF_SCREENS.pan_verification));
          }, 5000);
        } else if (response?.aadharStatus === IN_PROGRESS) {
          dispatch(disableLoader());
          setLoading(true);
          handleIssueWithAadharVerification(false);
        } else if (response?.aadharStatus === MISMATCH) {
          dispatch(disableLoader());
          dispatch(storeCafDocumentChanged(false));
          dispatch(storeReverifyAadharFlag(true));
          setLoading(true);
          handleIssueWithAadharVerification(true);
        } else if (response?.aadharStatus === FAILED) {
          dispatch(disableLoader());
          setLoading(false);
          sessionStorage.removeItem(AADHAR_RETRY_COUNT);
          localStorage.removeItem(NIUM_FOREX_CURRENT_SCREEN);
          dispatch(changeNiumForexScreen(NF_SCREENS.aadhar_verification));
        } else {
          setLoading(false);
          setShowMainScreen(true);
          dispatch(disableLoader());
          sessionStorage.removeItem(AADHAR_RETRY_COUNT);
          localStorage.removeItem(NIUM_FOREX_CURRENT_SCREEN);
        }
      }
    } else if (aadharRetryCount >= 3) {
      setLoading(false);
      setShowMainScreen(true);
      dispatch(disableLoader());
      sessionStorage.removeItem(AADHAR_RETRY_COUNT);
      localStorage.removeItem(NIUM_FOREX_CURRENT_SCREEN);
    }
  };

  const handleIssueWithAadharVerification = async (skipRetryLogic: boolean) => {
    // @ts-ignore
    const tryAgainFlag: boolean = await ModalFactory.builder?.(
      (onClose: any) => (
        <RetryAadharModal
          headingText={
            skipRetryLogic
              ? AADHAR_VERIFICATION?.aadharNumberMismatch
              : AADHAR_VERIFICATION?.aadharNumberIssue
          }
          subHeadingText={AADHAR_VERIFICATION?.tryAgainAadharVerification}
          submitButtonText={AADHAR_VERIFICATION?.tryAgain}
          handleTryAgain={() => {
            onClose(true);
          }}
        />
      ),
      'nium-fores retry-aadhar insta-modal center-modal',
      true
    );

    const aadharRetryCount: number = Number(sessionStorage.getItem(AADHAR_RETRY_COUNT) || 0) || 1;

    sessionStorage.setItem(AADHAR_RETRY_COUNT, (aadharRetryCount + 1).toString());
    if (tryAgainFlag && aadharRetryCount < 3 && !skipRetryLogic) {
      setTimeout(() => {
        handleGetUserAadharStatus();
      }, 3000);
    } else if (aadharRetryCount >= 3 && !skipRetryLogic) {
      localStorage.removeItem(NIUM_FOREX_CURRENT_SCREEN);
      sessionStorage.removeItem(AADHAR_RETRY_COUNT);
      setLoading(false);
      setShowMainScreen(true);
      dispatch(disableLoader());
    } else if (skipRetryLogic && tryAgainFlag) {
      localStorage.removeItem(NIUM_FOREX_CURRENT_SCREEN);
      sessionStorage.removeItem(AADHAR_RETRY_COUNT);
      setLoading(false);
      setShowMainScreen(true);
      dispatch(disableLoader());
    }
  };

  const handleIsUserApproved = () => {
    // reVerifyAadhar is used when user confirms details changed or came back from Pan Screen.
    if (status === APPROVED && !reVerifyAadhar && !cafDocumentFlag && !reKycRequired) {
      dispatch(changeNiumForexScreen(NF_SCREENS.re_kyc));
    }
  };

  const getAadharStatus = async () => {
    dispatch(activateLoader());
    const res: any = await dispatch(getUserProfileData(true));
    if (res?.data?.data?.remitterDetails?.aadhar_status === VERIFIED) {
      setUserAadharStatusApproved(true);
    } else {
      setUserAadharStatusApproved(false);
    }
    dispatch(disableLoader());
  };

  useEffect(() => {
    dispatch(disableLoader());
    const isUserInitiatedAadharFlag =
      localStorage.getItem(NIUM_FOREX_CURRENT_SCREEN) === NF_SCREENS.aadhar_verification;
    const aadharUrl = currentURL.split('/').includes(NF_CALLBACK_URLS.aadharCallbackUrl);
    const triggerGetStatusAadharStatusFlag =
      isUserInitiatedAadharFlag && aadharUrl && !reVerifyAadhar;

    if (triggerGetStatusAadharStatusFlag) {
      setShowMainScreen(false);
      setLoading(true);
      setTimeout(() => {
        handleGetUserAadharStatus();
      }, 8000);
    }
    dispatch(disableLoader());
    //eslint-disable-next-line
  }, [tempTxnId]);

  useEffect(() => {
    getAadharStatus();
    handleIsUserApproved();
    // eslint-disable-next-line
    const match = currentURL.match(/\/(\d+)/);
    if (match) {
      setTempTxnId(parseInt(match[1]));
    }
    //eslint-disable-next-line
  }, []);

  return (
    <>
      {showMainScreen ? (
        <ParentContainer padding={[48, 0]}>
          <div className="navigationContainer">
            <img
              className="closeButton"
              onClick={handleCloseAadharVerification}
              src={CLOSE_POPUP_ICON}
              alt="close"
            />
          </div>
          <StepHeader
            heading={AADHAR_VERIFICATION?.varifyAadhar}
            headerLogo={ID_CARD_FILL_ICON}
            stepNumber={stepperData?.stepNumber}
            totalSteps={totalSteps}
            subHeading={AADHAR_VERIFICATION?.digiLockerConsent}
          />
          <FormContainer marginTop={48}>
            <div>
              <img src={DIGI_LOCKER_ADHAAR_ICON} alt="logo" width={175} height={151} />
            </div>
            <ButtonContainer>
              <div className="submitContainer">
                <CommonButton
                  onClick={handleSubmit}
                  text={AADHAR_VERIFICATION?.proceedToAadharVerification}
                  width={450}
                />
              </div>
              <p className="link" onClick={handleAadharMobileNotLinked}>
                {AADHAR_VERIFICATION?.aadharMobileNotLinked}
              </p>
            </ButtonContainer>
          </FormContainer>
        </ParentContainer>
      ) : (
        <AadharSuccess loading={loading} />
      )}
    </>
  );
};

const mapStateToProps = (reduxState: IStore) => {
  const {
    rootReducer: {
      niumForexReducer: {
        transactionDetails: { tempTransactionId },
        reVerifyAadhar,
        cafDocumentFlag,
        userDetails: { status }
      }
    }
  } = reduxState;

  return {
    tempTransactionId,
    reVerifyAadhar,
    cafDocumentFlag,
    status
  };
};

export default connect<any, any, any, IStore>(mapStateToProps, (dispatch: React.Dispatch<any>) => {
  return {
    activateLoader,
    disableLoader,
    getCaptureLink,
    getUserAadharStatus,
    storeTransactionDetails,
    getUserProfileData,
    storeCafDocumentChanged,
    dispatch,
    storeReverifyAadharFlag
  };
  //@ts-ignore
})(AadharVerificationScreen);
